import { motion } from "framer-motion";
import { Camera, FileDown, Headset, Layers3 } from "lucide-react";
import { FadeUp } from "@/components/landing/Reveal";

const Cell = ({ className = "", delay = 0, children, testid }) => (
  <FadeUp delay={delay} className={`group relative border border-white/10 bg-[#111722] overflow-hidden ${className}`} data-testid={testid}>
    <motion.div whileHover={{ y: -4 }} transition={{ type: "spring", stiffness: 260, damping: 22 }} className="h-full">
      {children}
    </motion.div>
  </FadeUp>
);

const Small = ({ icon: Icon, k, title, text, testid, delay }) => (
  <Cell className="md:col-span-4" delay={delay} testid={testid}>
    <div className="p-7 sm:p-8 h-full flex flex-col justify-between gap-10 min-h-[240px]">
      <div className="flex items-center justify-between">
        <Icon className="w-6 h-6 text-[#F59E0B]" strokeWidth={1.5} />
        <span className="font-mono text-xs text-[#F5F1E8]/50">{k}</span>
      </div>
      <div>
        <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight">{title}</h3>
        <p className="mt-2 text-sm text-[#F5F1E8]/70 leading-relaxed">{text}</p>
      </div>
    </div>
  </Cell>
);

export const Bento = () => (
  <section id="plataforma" className="relative mx-auto max-w-[1400px] px-5 sm:px-8 py-24 lg:py-36" data-testid="bento-section">
    <div className="grid lg:grid-cols-12 gap-8 mb-14 lg:mb-20">
      <FadeUp className="lg:col-span-5 font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]">/ 01 — La plataforma</FadeUp>
      <FadeUp delay={0.1} className="lg:col-span-7">
        <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.02]">
          Una inspección completa, de la planta al reporte, <span className="text-[#F5F1E8]/45">sin hojas de cálculo.</span>
        </h2>
      </FadeUp>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
      <Cell className="md:col-span-7 md:row-span-2 min-h-[420px] md:min-h-[520px]" testid="bento-questionnaire">
        <img src="/img/p2.jpg" alt="Ingeniero revisando controles de seguridad en planta" className="absolute inset-0 w-full h-full object-cover opacity-70 transition-transform duration-[1.6s] ease-out group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/50 to-transparent" />
        <div className="relative h-full p-7 sm:p-10 flex flex-col justify-end">
          <Layers3 className="w-7 h-7 text-[#F59E0B] mb-5" strokeWidth={1.5} />
          <h3 className="font-display-x text-3xl sm:text-5xl font-black uppercase tracking-tight leading-[0.95]">6 auditorías.<br />2 pilares.</h3>
          <p className="mt-4 max-w-md text-[#F5F1E8]/80">Cumplimiento legal STPS y calidad ISO 9001. Cada reactivo se responde con Sí, No o N/A, bloque por bloque, y cada centro de trabajo guarda su propio historial.</p>
        </div>
      </Cell>
      <Cell className="md:col-span-5" delay={0.08} testid="bento-score">
        <div className="p-7 sm:p-8 h-full flex flex-col justify-between gap-8 min-h-[250px]">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#F5F1E8]/60">Score de cumplimiento</span>
          <div className="font-mono text-sm sm:text-base text-[#F5F1E8]/85 leading-relaxed">
            <span className="text-[#F59E0B]">score</span> = Sí ÷ (reactivos − N/A) × 100
          </div>
          <div className="font-display-x text-7xl font-black tracking-tighter">91.6<span className="text-[#F59E0B] text-5xl">%</span></div>
        </div>
      </Cell>
      <Cell className="md:col-span-5" delay={0.14} testid="bento-risk">
        <div className="p-7 sm:p-8 h-full flex flex-col justify-between gap-6 min-h-[250px]">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#F5F1E8]/60">Nivel de riesgo</span>
          <div className="space-y-3">
            {[["Bajo", "≥ 80%", "bg-emerald-400", "w-full"], ["Medio", "60 – 79%", "bg-amber-400", "w-3/4"], ["Alto", "< 60%", "bg-rose-500", "w-1/2"]].map(([l, r, c, w]) => (
              <div key={l} className="flex items-center gap-4">
                <span className="w-16 text-sm font-semibold">{l}</span>
                <div className="flex-1 h-2 bg-white/5"><div className={`h-full ${c} ${w} origin-left transition-transform duration-700 group-hover:scale-x-[0.92]`} /></div>
                <span className="w-20 text-right font-mono text-xs text-[#F5F1E8]/65">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </Cell>
      <Small icon={Camera} k="EVIDENCIA" title="Foto en cada “No”" text="Al marcar un incumplimiento se habilita la carga de evidencia fotográfica del hallazgo." testid="bento-evidence" delay={0.05} />
      <Small icon={FileDown} k="REPORTE" title="PDF listo para firmar" text="Descarga el resumen con score, riesgo, respuestas y fotos en formato A4." testid="bento-pdf" delay={0.1} />
      <Small icon={Headset} k="ASCENTRA" title="Consultor a un clic" text="Solicita acompañamiento de Ascentra Consulting para cerrar tus brechas normativas." testid="bento-consultant" delay={0.15} />
    </div>
  </section>
);
